/**
 * ContentOpportunities — Consent Gate Dashboard
 *
 * Nothing generates without approval. Content managers see credit costs,
 * super admins also see EUR costs.
 *
 * Date: 2026-04-08
 */

import { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Lightbulb,
  Check,
  X,
  Clock,
  Loader2,
  Coins,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';

interface Opportunity {
  id: string;
  site_id: string;
  topic: string;
  opportunity_type: string;
  source: string;
  reasoning: string;
  proposed_action: string | null;
  evidence: Record<string, unknown>;
  estimated_impressions_monthly: number | null;
  estimated_revenue_monthly_eur: number | null;
  estimated_generation_cost_eur?: number | null;
  actual_generation_cost_eur?: number | null;
  credit_cost: number;
  priority_score: number;
  status: string;
  decided_by_email: string | null;
  decided_at: string | null;
  rejection_reason: string | null;
  created_at: string;
}

interface CreditBalance {
  tier: string;
  included: number;
  used: number;
  remaining: number;
}

interface StatusCounts {
  [key: string]: number;
}

const STATUS_FILTERS = ['proposed', 'approved', 'rejected', 'deferred'] as const;

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    proposed: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    deferred: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${variants[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    demand_gap: 'bg-blue-100 text-blue-800',
    content_refresh: 'bg-orange-100 text-orange-800',
    quality_improvement: 'bg-purple-100 text-purple-800',
    new_topic: 'bg-green-100 text-green-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-600'}`}>
      {type?.replace(/_/g, ' ')}
    </span>
  );
}

function EvidenceChips({ evidence }: { evidence: Record<string, unknown> }) {
  const entries = Object.entries(evidence || {}).slice(0, 4);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {entries.map(([key, val]) => (
        <span key={key} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600">
          {key}: {typeof val === 'number' ? val.toLocaleString() : String(val).slice(0, 30)}
        </span>
      ))}
    </div>
  );
}

export default function ContentOpportunities() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [counts, setCounts] = useState<StatusCounts>({});
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('proposed');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedSite?.id) return;
    setLoading(true);
    setError(null);

    try {
      const res = await authFetch(
        `/api/content-opportunities/consent/list?siteId=${selectedSite.id}&status=${statusFilter}`
      );
      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
      const data = await res.json();
      setOpportunities(data.opportunities || []);
      setCounts(data.counts || {});
      setCreditBalance(data.creditBalance || null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (id: string, action: 'approve' | 'reject' | 'defer', reason?: string) => {
    setActionLoading(id);
    try {
      const body: Record<string, string> = {};
      if (action === 'reject') {
        const input = reason || prompt('Rejection reason:');
        if (!input) { setActionLoading(null); return; }
        body.reason = input;
      }

      const res = await authFetch(
        `/api/content-opportunities/consent/${id}/${action}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `Action failed: ${res.status}`);
      }
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to view content opportunities.</AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Content Opportunities</h1>
          <p className="text-muted-foreground text-sm">Review and approve content generation proposals</p>
        </div>
        {creditBalance && (
          <Card className="w-64">
            <CardContent className="p-3 flex items-center gap-3">
              <Coins className="h-5 w-5 text-amber-500" />
              <div>
                <div className="text-sm font-medium">
                  {creditBalance.remaining} credits remaining
                </div>
                <div className="text-xs text-muted-foreground">
                  {creditBalance.used} / {creditBalance.included} used this month ({creditBalance.tier})
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2">
        {STATUS_FILTERS.map(s => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {counts[s] != null && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-white/20">
                {counts[s]}
              </span>
            )}
          </Button>
        ))}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      ) : opportunities.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Lightbulb className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>No {statusFilter} opportunities for this site.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {opportunities.map(opp => (
            <Card key={opp.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base flex items-center gap-2">
                      {opp.topic}
                      <TypeBadge type={opp.opportunity_type} />
                      <StatusBadge status={opp.status} />
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                      {opp.reasoning}
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground whitespace-nowrap ml-4">
                    <div className="font-medium">{opp.credit_cost} credit{opp.credit_cost !== 1 ? 's' : ''}</div>
                    {isSuperAdmin && opp.estimated_generation_cost_eur != null && (
                      <div className="text-xs">~{Number(opp.estimated_generation_cost_eur).toFixed(3)} EUR</div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {opp.estimated_impressions_monthly != null && (
                      <span className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        {opp.estimated_impressions_monthly.toLocaleString()} imp/mo
                      </span>
                    )}
                    {opp.source && (
                      <span>Source: {opp.source}</span>
                    )}
                    {opp.priority_score != null && (
                      <span>Priority: {opp.priority_score}</span>
                    )}
                    {isSuperAdmin && opp.estimated_revenue_monthly_eur != null && (
                      <span>~{Number(opp.estimated_revenue_monthly_eur).toFixed(2)} EUR/mo</span>
                    )}
                  </div>

                  {opp.status === 'proposed' || opp.status === 'deferred' ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAction(opp.id, 'defer')}
                        disabled={actionLoading === opp.id || opp.status === 'deferred'}
                      >
                        {actionLoading === opp.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Clock className="h-3 w-3" />}
                        <span className="ml-1">Defer</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleAction(opp.id, 'reject')}
                        disabled={actionLoading === opp.id}
                      >
                        <X className="h-3 w-3" />
                        <span className="ml-1">Reject</span>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleAction(opp.id, 'approve')}
                        disabled={actionLoading === opp.id}
                      >
                        {actionLoading === opp.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        <span className="ml-1">Approve</span>
                      </Button>
                    </div>
                  ) : opp.status === 'rejected' && opp.rejection_reason ? (
                    <span className="text-xs text-red-600">
                      Reason: {opp.rejection_reason}
                    </span>
                  ) : opp.decided_by_email ? (
                    <span className="text-xs text-muted-foreground">
                      by {opp.decided_by_email} on {new Date(opp.decided_at!).toLocaleDateString()}
                    </span>
                  ) : null}
                </div>
                <EvidenceChips evidence={opp.evidence} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
